import React from 'react';
import '../modern.css';
import PageLayout from './PageLayout';
import SectionHeading from './SectionHeading';
import ContentCard from './ContentCard';
import useReveal from '../useReveal';
import { pageContent } from './pageContent';

function About() {
  useReveal();
  return (
    <PageLayout
      pageKey="about"
      heroVisual={
        <div className="hero-system" aria-hidden="true">
          <div className="hero-system__bar">
            <span className="hs-tag">Signal &middot; Operating picture</span>
            <span className="hs-live"><i className="hs-live-dot" />Live</span>
          </div>

          <div className="hero-system__stages">
            <div className="hs-stage hs-stage--1">
              <span className="hs-node">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </span>
              <span className="hs-meta">
                <strong>Understand</strong>
                <em>Evidence, context, constraints</em>
              </span>
              <span className="hs-viz hs-viz--bars"><i /><i /><i /><i /><i /></span>
            </div>

            <div className="hs-link"><i className="hs-packet hs-packet--1" /></div>

            <div className="hs-stage hs-stage--2">
              <span className="hs-node">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </span>
              <span className="hs-meta">
                <strong>Reason</strong>
                <em>Models applied with judgement</em>
              </span>
              <span className="hs-viz hs-viz--rings"><i /><i /><b /></span>
            </div>

            <div className="hs-link"><i className="hs-packet hs-packet--2" /></div>

            <div className="hs-stage hs-stage--3">
              <span className="hs-node">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="14" rx="2" />
                  <line x1="8" y1="22" x2="16" y2="22" />
                  <line x1="12" y1="18" x2="12" y2="22" />
                </svg>
              </span>
              <span className="hs-meta">
                <strong>Deliver</strong>
                <em>Working products, measured outcomes</em>
              </span>
              <span className="hs-viz hs-viz--check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline className="hs-check-path" points="4 12.5 9.5 18 20 6" />
                </svg>
              </span>
            </div>
          </div>

          <div className="hero-system__foot">
            <span className="hs-caption">Evidence in &mdash; outcomes out</span>
            <span className="hs-rail"><i /></span>
          </div>
        </div>
      }
    >

      <section className="story section">
        <div className="container">
          <SectionHeading {...pageContent.about.sections.story} alignLeft />
          <p style={{fontSize: '1.05rem', color: 'var(--text-soft)', lineHeight: '1.8', maxWidth: '720px', margin: 0}}>
            Databuilder was founded after more than a decade working at the intersection of data, AI and public services.
          </p>
          <p style={{fontSize: '1.05rem', color: 'var(--text-soft)', lineHeight: '1.8', maxWidth: '720px', marginTop: '1rem'}}>
            Throughout my career across local government, housing and national public sector organisations, I repeatedly saw talented teams with ambitious goals but without access to the specialist expertise needed to turn ideas into reality. While the world&rsquo;s leading technology companies invest heavily in world-class engineering, product development and AI capability, many public sector organisations are expected to deliver equally important outcomes with only a fraction of the resources.
          </p>
          <p style={{fontSize: '1.05rem', color: 'var(--text-soft)', lineHeight: '1.8', maxWidth: '720px', marginTop: '1rem'}}>
            That never felt right. The public sector delivers some of society&rsquo;s most important services. The people delivering those services deserve access to the same quality of technology, data and AI expertise that the best private sector organisations take for granted.
          </p>
          <p style={{fontSize: '1.05rem', color: 'var(--text-soft)', lineHeight: '1.8', maxWidth: '720px', marginTop: '1rem'}}>
            Databuilder was created to help close that gap. But the motivation goes beyond technology. Too many projects become reports, pilots and proof-of-concepts that never create meaningful change. I&rsquo;ve always been driven by building products that are useful, complete, trusted and genuinely improve people&rsquo;s lives. Technology should solve real problems, be safe by design, and leave organisations more capable than before.
          </p>
          <p style={{fontSize: '1.05rem', color: 'var(--text-soft)', lineHeight: '1.8', maxWidth: '720px', marginTop: '1rem'}}>
            That&rsquo;s why Databuilder combines deep technical expertise with practical delivery experience, helping organisations move from ideas and ambition to working products, measurable outcomes and lasting capability. Because AI should not just be innovative. It should be responsible, effective and make society better.
          </p>
          <p style={{fontSize: '1.02rem', color: 'var(--text-soft)', lineHeight: '1.6', maxWidth: '720px', marginTop: '1.5rem', marginBottom: 0, letterSpacing: '0.08em', textTransform: 'uppercase'}}>
            <strong style={{fontSize: '1.15rem', letterSpacing: '0.12em', color: 'var(--text)'}}>Tom Foster</strong><br />
            <span style={{fontWeight: 600, color: 'var(--text-soft)'}}>Founder &amp; Chief AI Officer</span>
          </p>
        </div>
      </section>

      <section className="team section section--alt">
        <div className="container">
          <SectionHeading {...pageContent.about.sections.founder} />
          <div className="founders-grid">
            <div className="founder-card">
              <div className="founder-image">
                <img src="images/tom.png" alt="Tom Foster" />
              </div>
              <h4>Tom Foster</h4>
              <span className="title">Founder &amp; Chief AI Officer</span>
              <p>Tom Foster is an applied AI leader, architect and data scientist with over a decade of experience delivering AI, machine learning and data transformation across government, housing, health and regulated sectors. He brings hands-on engineering depth and board-level strategic thinking to the organisations that need AI to work in the real world.</p>
              <p style={{marginTop: '0.75rem'}}>He has led AI programmes from early discovery through to deployment and organisational adoption, spanning production RAG and GraphRAG systems, multi-agent workflows, geospatial AI, machine learning products and responsible AI governance in complex public-sector environments.</p>
              <p style={{marginTop: '0.75rem'}}>Before founding Databuilder, Tom led applied AI and data science initiatives across local government and the wider public sector, and previously served in a strategic data and AI leadership role within a national regulator. That experience helped shape his practical, governance-led approach to turning AI ambition into operational outcomes grounded in evidence and delivery.</p>

              <div className="experience">
                <h5>Selected Impact</h5>
                <ul>
                  <li>Reduced a staff-survey analysis process from approximately four months to one hour through a production GraphRAG solution.</li>
                  <li>Designed and scaled an AI planning assistant used across five local authority partners.</li>
                  <li>Delivered more than &pound;100,000 annual cloud cost savings through data platform redesign and a further &pound;70,000 annual licensing avoidance through modern entity-resolution approaches.</li>
                  <li>Created EPC Complete, a machine learning product supporting public-health, climate, fire-risk and financial-inclusion use cases, used by multiple councils.</li>
                  <li>Built production-ready multi-agent workflow for highly regulated environments with full audit trail and human oversight.</li>
                  <li>Invited speaker at Cambridge University on AI governance, safety and ethics in the public sector.</li>
                </ul>
              </div>

              <p style={{marginTop: '1rem'}}><strong>Why organisations choose Databuilder:</strong> Unlike many AI advisors, he has personally built and deployed production AI systems. Unlike many engineers, he also operates at senior leadership level, shaping strategy, governance and investment decisions. That mix of delivery and leadership is what makes Databuilder effective in government and regulated environments.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <SectionHeading {...pageContent.about.sections.principles} />
        <div className="pitch-grid">
          {pageContent.about.cards.principles.map((card) => (
            <ContentCard
              key={card.title}
              className="pitch-card"
              icon={card.icon}
              title={card.title}
              description={card.description}
            />
          ))}
        </div>
      </section>

    </PageLayout>
  );
}

export default About;
